import { type ContentHref, ContentType } from '@generalTypes/apiTypes';
import {
  selectApiWithPendingChanges,
  selectApiWithPendingChangesRelationsToAndFromMap,
  selectBaselineRelationsToAndFromMap,
} from '@newStore/documentApi/documentApiSelectors';
import { stringDiffArrowFunction } from '@newStore/documentUI/transformProposal/asideDiffText';
import { getPathToRoot } from '@newStore/externalData/externalDataHelpers';
import { createTypedSelector } from '@newStore/genericHelpers';
import { getGoalIdentifier } from '@newStore/llinkid/llinkidHelpers';
import type { AsideChangeMessageSelector, EditComponent } from '@nodeTypeConfig/configTypes';

export const selectLeerlijnReferenceGoals = createTypedSelector(
  [
    (state) => selectApiWithPendingChanges(state).content,
    (state) => selectApiWithPendingChangesRelationsToAndFromMap(state),
  ],
  (content, relationsMap) => {
    const goals = Object.values(content).filter(
      (c) => c.type === ContentType.CURRICULUM_ZILL_GENERIC_GOAL,
    );
    return goals.map((goal) => {
      const pathToRoot = getPathToRoot(relationsMap, content, goal.$$meta.permalink);

      const zillDevelopmentField = pathToRoot.find(
        (path) => path.type === ContentType.CURRICULUM_ZILL_DEVELOPMENT_FIELD,
      );

      return {
        href: goal.$$meta.permalink,
        key: goal.key,
        identifier: getGoalIdentifier(pathToRoot.slice(0, -2)) || '',
        color: zillDevelopmentField?.color || undefined,
        title: goal.title || '',
      };
    });
  },
);

export const selectLeerlijnReference = createTypedSelector(
  [
    (state) => selectApiWithPendingChangesRelationsToAndFromMap(state),
    (state) => state.documentUI.currentEditingNode as ContentHref,
  ],
  (relationsMap, href) => {
    return relationsMap.from[href]?.find((rel) => rel.relationtype === 'REFERENCES');
  },
);

const selectLeerlijnReferenceOldValue = createTypedSelector(
  [
    (state) => selectBaselineRelationsToAndFromMap(state),
    (state) => state.documentUI.currentEditingNode as ContentHref,
  ],
  (rawRelationsMap, href) => {
    return rawRelationsMap.from[href]?.find((rel) => rel.relationtype === 'REFERENCES');
  },
);

export const selectLeerlijnReferenceSelectedGoal = createTypedSelector(
  [(state) => selectLeerlijnReference(state), (state) => selectLeerlijnReferenceGoals(state)],
  (relation, goals) => {
    return goals.find((goal) => goal.href === relation?.to.href);
  },
);

const selectLeerlijnReferenceOldGoal = createTypedSelector(
  [
    (state) => selectLeerlijnReferenceOldValue(state),
    (state) => selectLeerlijnReferenceGoals(state),
  ],
  (relation, goals) => {
    return goals.find((goal) => goal.href === relation?.to.href);
  },
);

const getGoalLabel = (goal) => {
  return goal?.identifier ? `${goal.identifier} - ${goal.title}` : goal?.title || null;
};

export const selectChangeMessageForLeerlijnReference: AsideChangeMessageSelector<EditComponent> =
  createTypedSelector(
    [
      (state) => selectLeerlijnReferenceOldGoal(state),
      (state) => selectLeerlijnReferenceSelectedGoal(state),
    ],
    (oldGoal, newGoal): string | null => {
      const newValue = getGoalLabel(newGoal);
      const oldValue = getGoalLabel(oldGoal);
      return stringDiffArrowFunction(newValue, oldValue);
    },
  );
